/**
 * 解析 expire_time 为 UTC 毫秒时间戳。
 * ISO 8601 带 Z（如 2026-11-16T07:35:07.623Z）会被正确解析为绝对时刻，与用户本地时区无关。
 */
export const parseExpireMs = raw => {
  if (raw == null || raw === '') return NaN
  const ms = new Date(raw).getTime()

  return Number.isFinite(ms) ? ms : NaN
}

/** 订阅仍有效：无 expire_time 视为有效；否则 expire_time >= 当前时刻 */
export const isSubscriptionActive = (expireTime, now = Date.now()) => {
  const ms = parseExpireMs(expireTime)
  if (!Number.isFinite(ms)) return true

  return ms >= now
}

export const isDeviceActive = device => isSubscriptionActive(device?.expire_time)

export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

/** 已过期且仍在 30 天宽限期内；恰好第 30 天仍符合，未过期或超过 30 天均不符合 */
export const isExpiredWithinGracePeriod = (expireTime, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const ms = parseExpireMs(expireTime)
  if (!Number.isFinite(ms) || !Number.isFinite(now) || ms >= now) return false

  return now - ms <= graceMs
}

/** 已过期且超过宽限期（默认 30 天） */
export const isExpiredPastGracePeriod = (expireTime, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const ms = parseExpireMs(expireTime)
  if (!Number.isFinite(ms)) return false
  if (ms >= now) return false

  return now - ms > graceMs
}

/** 全部设备均已过期且超过宽限期 */
export const areAllDevicesExpiredPastGrace = (devices, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const list = Array.isArray(devices) ? devices : []
  if (!list.length) return false

  return list.every(device => isExpiredPastGracePeriod(device?.expire_time, now, graceMs))
}

/** 从侧栏设备项（数组或单条记录）解析 expire_time */
export const resolveDeviceExpireTime = item => {
  if (!item) return null
  if (item.expire_time != null && item.expire_time !== '') return item.expire_time
  if (Array.isArray(item) && item[0]?.DeviceUser?.expire_time) return item[0].DeviceUser.expire_time
  if (item?.DeviceUser?.expire_time) return item.DeviceUser.expire_time

  return null
}

/** 侧栏仍展示：未过期、无 expire_time、或过期未超过 graceDays（默认 30 天） */
export const isDeviceWithinGracePeriod = (expireTime, now = Date.now(), graceMs = THIRTY_DAYS_MS) => {
  const ms = parseExpireMs(expireTime)
  if (!Number.isFinite(ms)) return true
  if (ms >= now) return true

  return now - ms <= graceMs
}

/** Feature 详情页遮罩：已过期且在宽限期内（默认 30 天） */
export const shouldShowExpiredMask = (expireTime, now = Date.now(), graceMs = THIRTY_DAYS_MS) =>
  !isSubscriptionActive(expireTime, now) && isDeviceWithinGracePeriod(expireTime, now, graceMs)

/** 取第一个可选中设备：优先未过期(active)，其次宽限期(grace)；永不返回超 30 天失效 */
export const pickFirstActiveDevice = (devices, platformType) => {
  const list = Array.isArray(devices) ? devices : []
  const scoped = platformType ? list.filter(item => item?.platform_type === platformType) : list
  const now = Date.now()
  const selectable = scoped.filter(item => {
    const ms = parseExpireMs(item?.expire_time)
    if (!Number.isFinite(ms)) return true
    if (ms >= now) return true

    return now - ms <= THIRTY_DAYS_MS
  })
  if (!selectable.length) return null

  return selectable.find(isDeviceActive) || selectable[0] || null
}

export const buildDeviceUserPayload = device => ({
  username: device.iCloud_username,
  password: device.iCloud_password,
  temporary: device.temporary,
  expire_time: device.expire_time,
  likeType: device.like_type,
  device_id: device?.id || device?.device_id || '',
  order_id: device?.order_id ?? device?.orderId ?? null,
  subscription_status: device?.subscription_status ?? null
})
